import constants from '@/constants';
import * as helpIds from '@/helpIds';
import gfppDataUtils from '../../utils/gfppDataUtils';
import { sections } from '@/util';
import {
  EditorRestrictionsApiKey,
  QuickEditApiKey,
  DesignerPanelsApiKey,
} from '@/apis';

import type { CompRef } from 'types/documentServices';
import type { EditorAPI } from '@/editorAPI';
import type { GFPPAction } from '@wix/editor-component-types';
import experiment from 'experiment';

const { ACTIONS } = constants.ROOT_COMPS.GFPP;

const toggleDesignPanel = gfppDataUtils.getTogglePanelFn(ACTIONS.DESIGN);

function getDesignerScanAction(): GFPPAction[] {
  return experiment.isOpen('se_addDesignerSection')
    ? [
        {
          label: 'Scan Footer',
          shouldTranslate: false,
          onClick: (editorAPI: EditorAPI, compRefs: CompRef[]) => {
            const designerPanelsApi =
              editorAPI.host.getAPI(DesignerPanelsApiKey);
            designerPanelsApi.openDesignerScanPresetPanel(compRefs[0]);
          },
        },
      ]
    : [];
}

function getChangeBackgroundAction(editorAPI: EditorAPI) {
  const footerRef = editorAPI.siteSegments.getFooter();
  const { label, icon, tooltip, backgroundMediaType } =
    gfppDataUtils.getDataGfppWithBackgroundIcon(editorAPI, footerRef, {
      label: 'gfpp_mainaction_footer_background',
      tooltip: '',
    });

  return {
    label,
    isApplied: true,
    isSelected: gfppDataUtils.getPanelStateFn(ACTIONS.BACKGROUND),
    onClick: gfppDataUtils.getTogglePanelFn(ACTIONS.BACKGROUND, {
      selectedComponent: [footerRef],
      biParams: { bg_media_type: backgroundMediaType },
    }),
    icon,
    tooltip,
  };
}

export default {
  untranslatable: true,
  mainActions: (editorAPI: EditorAPI, compRef: CompRef) => {
    const designAction = {
      label: editorAPI.zoomMode.isStageZoomMode()
        ? 'RightClick_Menu_Footer_Zoom_Out_Mode_Zoom_Label'
        : 'gfpp_mainaction_footer',
      isApplied: true,
      isSelected: gfppDataUtils.getPanelStateFn(ACTIONS.DESIGN),
      onClick: async (
        editorAPI: EditorAPI,
        compRef?: CompRef[],
        origin?: string,
        optionalPropsOverrides?: Record<string, unknown>,
      ) => {
        if (editorAPI.zoomMode.isStageZoomMode()) {
          const compLayout = editorAPI.components.layout.getRelativeToScreen(
            editorAPI.siteSegments.getFooter(),
          );

          await editorAPI.zoomMode.exitZoomMode({
            biParams: { origin },
            originalScrollY: compLayout.y,
          });
        }

        toggleDesignPanel(editorAPI, compRef, origin, optionalPropsOverrides);
      },
    };

    const backgroundAction = getChangeBackgroundAction(editorAPI);

    const quickEditAction = {
      label: `gfpp_mainaction_edit_section_label`,
      onClick: (editorAPI: EditorAPI, compRefs: CompRef[], origin: string) => {
        const quickEditAPI = editorAPI.host.getAPI(QuickEditApiKey);
        quickEditAPI.openPanel({
          rootControlCompRef: compRefs[0],
          origin,
        });
      },
    };
    const isQuickEditAvailable = editorAPI.host
      .getAPI(QuickEditApiKey)
      .isQuickEditAvailable();
    const quickEditActionIfAvailable = isQuickEditAvailable
      ? [quickEditAction]
      : [];
    const editorRestrictionsApi = editorAPI.host.getAPI(
      EditorRestrictionsApiKey,
    );
    const isFooterGfppVisible = editorRestrictionsApi.allowed(
      'gfpp_footer.visible',
    );
    const designerScanAction = getDesignerScanAction();
    const designActionIfAvailable = isFooterGfppVisible
      ? [
          gfppDataUtils.siteSegmentBackgroundSupported(editorAPI, compRef)
            ? backgroundAction
            : designAction,
        ]
      : [];

    return [
      ...designerScanAction,
      ...designActionIfAvailable,
      ...quickEditActionIfAvailable,
    ];
  },
  enabledActions: (editorAPI: EditorAPI, compRef: CompRef) => {
    const potentialActions = gfppDataUtils.siteSegmentBackgroundSupported(
      editorAPI,
      compRef,
    )
      ? [ACTIONS.DESIGN]
      : [];

    return sections.isSectionsEnabled()
      ? [...potentialActions, ACTIONS.SETTINGS, ACTIONS.HELP]
      : [ACTIONS.SETTINGS, ACTIONS.DESIGN, ACTIONS.HELP];
  },
  mobileEnabledActions: [ACTIONS.ANIMATION, ACTIONS.HELP],
  presetActions: {
    help: {
      helpId: helpIds.GFPP.FOOTER,
    },
  },
};
